'use client'

import { useMemo, useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import '@components/ui/carousel/nested-cards-carousel/styles.css'

// Lazy-load heavy carousel library (react-stacked-center-carousel)
const NestedCardsCarousel = dynamic(
  () =>
    import('@components/ui/carousel/nested-cards-carousel/NestedCardsCarousel').then(
      (m) => ({ default: m.NestedCardsCarousel }),
    ),
  { ssr: false },
)
import type { NestedCarouselCard } from '@components/ui/carousel/nested-cards-carousel/NestedCardsCarousel'
import type { HomePageFundsClientPayload } from '@domain/landing/homePageFundPayload'
import { transformFundsToCarouselData } from './LandinHeroPreIpoCarousel'
import { formatNumberWithShortcuts } from '@utils/format.utils'

type LandingHeroPreIPOCarouselClientProps = {
  homeFunds: HomePageFundsClientPayload
}

/**
 * Desktop: same positions/scales as react-stacked-center-carousel
 * (carouselWidth=800, slideWidth=290, defaultScaleFactor=0.85).
 */
const CAROUSEL_WIDTH_DESKTOP = 800
const SLIDE_WIDTH_DESKTOP = 290
const DEFAULT_SCALE_FACTOR = 0.85

/**
 * Mobile: center card 260px, side cards scaled 0.85 (smaller).
 * Cards named 2,1,0,3,4 from left to right (i=0..4).
 * With scale 0.85, visual half-width = 130*0.85 = 110.5px.
 * Cards 1 & 3 (inner, ±42.5): peek 23px beyond center card edge (130+23-110.5=42.5).
 * Cards 2 & 4 (outer, ±62.5): peek 20px beyond inner card's visible edge (42.5+110.5+20-110.5=62.5).
 * Cards 2 & 4 are compact: 260px height vs 400px for center/inner.
 * Breakpoint sm = 904px (tailwind).
 */
const SLIDE_WIDTH_MOBILE = 260
const MOBILE_SCALE_SIDE = 0.85
const MOBILE_POSITIONS = [-62.5, -42.5, 0, 42.5, 62.5] as const

type LayoutItem = { position: number; scale: number; zIndex: number }

function getPlaceholderSlideLayoutDesktop(): LayoutItem[] {
  const carouselWidth = CAROUSEL_WIDTH_DESKTOP
  const slideWidth = SLIDE_WIDTH_DESKTOP
  const maxVisibleSlide = 5
  const slidePerSide = Math.max((maxVisibleSlide - 1) / 2 + 1, 1)
  const renderedSlidePerSide = (maxVisibleSlide + 1) / 2
  const availableSpace = carouselWidth / 2 - slideWidth / 2
  const scales: number[] = [1]
  const scaledSlideWidths: number[] = [slideWidth]
  for (let slide = 1; slide <= slidePerSide; slide++) {
    const scale = Math.pow(DEFAULT_SCALE_FACTOR, slide)
    scales.push(scale)
    scaledSlideWidths.push(slideWidth * scale)
  }
  const includedSlideWidths = scaledSlideWidths.slice(1)
  const totalSlideWidth = includedSlideWidths.reduce((a, b) => a + b, 0)
  const offSetPercentage = totalSlideWidth ? availableSpace / totalSlideWidth : 0
  const scaledOffsets: number[] = [0]
  const offsets: number[] = [0]
  for (let slide = 1; slide <= slidePerSide; slide++) {
    const scale = scales[slide]
    scaledOffsets[slide] = slideWidth * scale * offSetPercentage
    offsets[slide] = scaledOffsets[slide] + slideWidth * ((1 - scale) / 2)
  }
  const result: LayoutItem[] = []
  let filledWidth = 0
  for (let absIndex = 0; absIndex <= slidePerSide; absIndex++) {
    const offset = offsets[absIndex]
    const currentOffSet = filledWidth + offset
    const scale = scales[absIndex]
    const zIndex = renderedSlidePerSide - absIndex
    if (absIndex === 0) {
      result.push({ position: 0, scale, zIndex })
    } else {
      result.push({
        position: -currentOffSet,
        scale,
        zIndex: renderedSlidePerSide - absIndex,
      })
      result.push({ position: currentOffSet, scale, zIndex })
    }
    if (absIndex !== 0) filledWidth += scaledOffsets[absIndex]
  }
  result.sort((a, b) => a.position - b.position)
  return result.slice(1, 6)
}

/** Mobile layout: center full size, inner cards (1&3) at ±42.5 (peek 23px), outer cards (2&4) at ±62.5 (peek 20px behind inner). */
function getPlaceholderSlideLayoutMobile(): LayoutItem[] {
  return MOBILE_POSITIONS.map((position, i) => ({
    position,
    scale: position === 0 ? 1 : MOBILE_SCALE_SIDE,
    zIndex: position === 0 ? 3 : i === 1 || i === 3 ? 2 : 1,
  }))
}

const SSR_SLIDE_LAYOUT_DESKTOP = getPlaceholderSlideLayoutDesktop()
const SSR_SLIDE_LAYOUT_MOBILE = getPlaceholderSlideLayoutMobile()

type CarouselCard =
  | NestedCarouselCard
  | ReturnType<typeof transformFundsToCarouselData>[number]

function PlaceholderMetric({ item }: { item: CarouselCard }) {
  const metricLabel = 'metricLabel' in item ? item.metricLabel : undefined
  const footerMode = 'footerMode' in item ? item.footerMode : undefined
  const gainPct = 'gainPct' in item ? item.gainPct : undefined
  const hasGain = typeof gainPct === 'number' && Number.isFinite(gainPct)

  if (metricLabel) {
    return (
      <div className="flex flex-col ml-auto">
        <h2 className="text-xl uppercase text-[#1D1C1F] font-medium text-right whitespace-nowrap">
          {metricLabel}
        </h2>
      </div>
    )
  }

  if (footerMode === 'pct' && hasGain) {
    const rounded = Math.round(gainPct)
    const up = gainPct >= 0
    return (
      <div className="flex flex-col ml-auto">
        <h2
          className={`text-xl uppercase font-medium text-right whitespace-nowrap ${
            up ? 'text-cw-growth-green' : 'text-cw-system-red'
          }`}
        >
          {`${rounded > 0 ? '+' : ''}${rounded}%`}
        </h2>
      </div>
    )
  }

  return (
    <div className="flex flex-col ml-auto">
      <h2 className="text-xl uppercase text-[#1D1C1F] font-medium text-right whitespace-nowrap">
        $ {formatNumberWithShortcuts(item.currentValuation)}
      </h2>
      <p className="text-xs uppercase text-[#1D1C1F] text-right">Valuation</p>
    </div>
  )
}

const slideIndices = [-2, -1, 0, 1, 2] as const
const getModIndex = (n: number, m: number) => ((n % m) + m) % m

function placeholderAriaLabel(item: CarouselCard) {
  const metricLabel = 'metricLabel' in item ? item.metricLabel : undefined
  const footerMode = 'footerMode' in item ? item.footerMode : undefined
  const gainPct = 'gainPct' in item ? item.gainPct : undefined
  if (metricLabel) return `${item.title}, ${metricLabel}`
  if (footerMode === 'pct' && typeof gainPct === 'number' && Number.isFinite(gainPct)) {
    const rounded = Math.round(gainPct)
    return `${item.title}, ${rounded > 0 ? '+' : ''}${rounded}%`
  }
  return `${item.title}, $ ${formatNumberWithShortcuts(item.currentValuation)}`
}

function PlaceholderCardContent({
  item,
  slideWidth,
  compact = false,
  priority = false,
}: {
  item: CarouselCard
  slideWidth: number
  compact?: boolean
  priority?: boolean
}) {
  const href = 'href' in item ? item.href : undefined
  const cardBody = (
    <>
      <img
        src={item.image}
        alt={item.title}
        width={slideWidth}
        height={280}
        className="flex-1 w-full h-full object-cover user-select-none"
        draggable={false}
        fetchPriority={priority ? 'high' : 'auto'}
        loading={priority ? 'eager' : 'lazy'}
      />
      <div className="flex py-3 sm:py-4 px-5 sm:px-6 w-full mt-auto bg-white font-funnel items-center">
        <div className="flex flex-col">
          <h3 className="text-base font-bold text-cw-midnight-purple">
            {item.title.slice(0, 1)}
            <span className="uppercase">{item.title.slice(1)}</span>
          </h3>
          {item.description ? (
            <p className="text-xs uppercase text-[#1D1C1F]">{item.description}</p>
          ) : null}
        </div>
        <PlaceholderMetric item={item} />
      </div>
    </>
  )

  return (
    <div style={{ height: compact ? 306 : 400, paddingTop: compact ? 0 : 40 }}>
      {href ? (
        <Link
          href={href}
          aria-label={placeholderAriaLabel(item)}
          draggable={false}
          className="stack-card relative cursor-pointer bg-[#1d004c]"
          style={{ width: '100%', height: compact ? 306 : 360 }}
        >
          {cardBody}
        </Link>
      ) : (
        <div
          className="stack-card relative bg-[#1d004c]"
          style={{ width: '100%', height: compact ? 306 : 360 }}
        >
          {cardBody}
        </div>
      )}
    </div>
  )
}

/** Desktop placeholder: visible from sm (904px) up. */
const SSRCarouselPlaceholderDesktop = ({ cards }: { cards: CarouselCard[] }) => {
  if (!cards.length) return null
  const centerPosition = CAROUSEL_WIDTH_DESKTOP / 2 - SLIDE_WIDTH_DESKTOP / 2
  return (
    <div
      className="relative mx-auto hidden sm:block"
      style={{
        width: '100%',
        maxWidth: 511,
        height: 480,
        top: '-40px',
        marginBottom: '-79px',
      }}
    >
      {SSR_SLIDE_LAYOUT_DESKTOP.map((layout, i) => {
        const slideIndex = slideIndices[i] ?? 0
        const dataIndex = getModIndex(slideIndex, cards.length)
        const item = cards[dataIndex]
        const leftPx = centerPosition + layout.position - SLIDE_WIDTH_DESKTOP / 2
        return (
          <div
            key={`d-${i}-${item.title}`}
            className="absolute"
            style={{
              width: SLIDE_WIDTH_DESKTOP,
              height: 400,
              left: leftPx,
              top: 40,
              transform: `scale(${layout.scale})`,
              transformOrigin: 'center center',
              zIndex: layout.zIndex,
            }}
          >
            <PlaceholderCardContent
              item={item}
              slideWidth={SLIDE_WIDTH_DESKTOP}
              priority={layout.position === 0}
            />
          </div>
        )
      })}
    </div>
  )
}

/** Mobile placeholder: center 260px, inner side cards peek 40px, outer side cards peek 20px behind inner. Visible below sm (904px). */
const SSRCarouselPlaceholderMobile = ({ cards }: { cards: CarouselCard[] }) => {
  if (!cards.length) return null
  return (
    <div
      className="relative block w-full overflow-hidden sm:hidden"
      style={{
        height: 480,
        top: '-40px',
        marginBottom: '-79px',
      }}
    >
      {SSR_SLIDE_LAYOUT_MOBILE.map((layout, i) => {
        const slideIndex = slideIndices[i] ?? 0
        const dataIndex = getModIndex(slideIndex, cards.length)
        const item = cards[dataIndex]
        const leftOffset = layout.position - SLIDE_WIDTH_MOBILE / 2
        const isOuter = i === 0 || i === 4
        return (
          <div
            key={`m-${i}-${item.title}`}
            className="absolute"
            style={{
              width: SLIDE_WIDTH_MOBILE,
              height: isOuter ? 306 : 400,
              left: `calc(50% + ${leftOffset}px)`,
              top: isOuter ? 104 : 40,
              transform: `scale(${layout.scale})`,
              transformOrigin: 'center center',
              zIndex: layout.zIndex,
            }}
          >
            <PlaceholderCardContent
              item={item}
              slideWidth={SLIDE_WIDTH_MOBILE}
              compact={isOuter}
              priority={layout.position === 0}
            />
          </div>
        )
      })}
    </div>
  )
}

const SSRCarouselPlaceholder = ({ cards }: { cards: CarouselCard[] }) => {
  if (!cards.length) return null
  return (
    <>
      <SSRCarouselPlaceholderDesktop cards={cards} />
      <SSRCarouselPlaceholderMobile cards={cards} />
    </>
  )
}

/** Same mount as homepage `/`: SSR placeholder, then client-only StackedCarousel. */
export function HomepageHeroStackedCards({ cards }: { cards: NestedCarouselCard[] }) {
  const [isMounted, setIsMounted] = useState(false)
  const [isCarouselReady, setIsCarouselReady] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!cards.length) return null

  return (
    <>
      {!isCarouselReady && <SSRCarouselPlaceholder cards={cards} />}
      {isMounted && (
        <div
          style={
            !isCarouselReady
              ? { position: 'absolute', opacity: 0, pointerEvents: 'none', width: '100%' }
              : undefined
          }
        >
          <NestedCardsCarousel data={cards} onReady={() => setIsCarouselReady(true)} />
        </div>
      )}
    </>
  )
}

// Client component for interactivity and updates
export const LandingHeroPreIPOCarouselClient = ({
  homeFunds,
}: LandingHeroPreIPOCarouselClientProps) => {
  const carouselData = useMemo(
    () => transformFundsToCarouselData(homeFunds.data),
    [homeFunds.data],
  )

  return <HomepageHeroStackedCards cards={carouselData} />
}
